import React, { useState, useEffect } from "react";
import { createStore, applyMiddleware } from "redux";
import { Provider, connect } from "react-redux";
import thunk from "redux-thunk";
import { Button } from "react-bootstrap";

import CustomAudioPlayer from "components/CustomAudioPlayer";
import rootReducer from "../reducers";
import { fetchAllCartItems, addCartItemRequest, cartItemIds, addPreview } from "../actions/index";

// Create Redux store
const store = createStore(rootReducer, applyMiddleware(thunk));

// Initialize cart items
store.dispatch(fetchAllCartItems());

const PresetPackShowComponent = connect(state => ({
  cart_items_ids: cartItemIds(state.cart_items),
  previews: state.previews
}))(({ presetPack, userOwnsProduct, productInCart, addToCartLink, discount, cart_items_ids, previews, dispatch }) => {
  const [isAdding, setIsAdding] = useState(false);
  const productId = presetPack.id;
  const isInCart = cart_items_ids.includes(productId.toString());
  const isOwned = userOwnsProduct;

  // Auto-load audio preview on mount so player shows the available audio
  useEffect(() => {
    if (presetPack.audio_preview_url) {
      const previewData = {
        id: productId,
        name: presetPack.name,
        preview: presetPack.audio_preview_url,
        image: presetPack.mid_image,
        manufacturer_name: presetPack.manufacturer_name
      };
      dispatch(addPreview(previewData));
    }
  }, [presetPack, productId, dispatch]);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    setIsAdding(true);
    try {
      await dispatch(addCartItemRequest(productId));
    } catch (error) {
      console.error("Failed to add item to cart:", error);
    } finally {
      setIsAdding(false);
    }
  };

  const handlePlayPreview = () => {
    if (!presetPack.audio_preview_url) {
      return;
    }

    // Check if preview is already loaded
    const isPreviewLoaded = previews && previews.length > 0 &&
      previews.some(p => p.id === productId);

    if (!isPreviewLoaded) {
      // Load the preview if not already loaded
      const previewData = {
        id: productId,
        name: presetPack.name,
        preview: presetPack.audio_preview_url,
        image: presetPack.mid_image,
        manufacturer_name: presetPack.manufacturer_name
      };
      dispatch(addPreview(previewData));
    }

    // Trigger play by dispatching a custom event that the audio player can listen for
    setTimeout(() => {
      const playEvent = new CustomEvent('play-preview-requested');
      window.dispatchEvent(playEvent);
    }, 100);
  };

    return (
    <div className="preset-pack-product" itemScope itemType="http://schema.org/Product">
      {/* Hero Section - Bootstrap Jumbotron Style */}
      <div className="jumbotron">
        <div className="row">
          <div className="col-lg">
            <h1 itemProp="name">{presetPack.name}</h1>
            <p itemProp="description">
              {presetPack.plugin_name} Preset Pack by {presetPack.manufacturer_name}
            </p>

            {presetPack.genres && presetPack.genres.length > 0 && (
              <div className="genre-tags mb-3">
                {presetPack.genres.map((genre, index) => (
                  <a
                    key={index}
                    href={`/preset_packs/genres/${genre.toLowerCase().replace(/\s+/g, '-')}`}
                    className="badge badge-primary me-2"
                  >
                    {genre}
                  </a>
                ))}
              </div>
            )}

            {presetPack.audio_preview_url && (
              <div className="audio-preview-section mb-3">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handlePlayPreview}
                >
                  <i className="fa fa-play me-2"></i>
                  Play Preview
                </button>
              </div>
            )}
          </div>

          <div className="col-lg text-right product-status">
            {isOwned ? (
              <div>
                <h5>You own {presetPack.name}</h5>
                <a href="/product_downloads" className="btn btn-primary download-button">
                  Download <i className="fa fa-download"></i>
                </a>
              </div>
            ) : isInCart ? (
              <h5>{presetPack.name} is already in your cart</h5>
            ) : (
              <div>
                {isAdding ? (
                  <button className="btn btn-lg btn-success" disabled>
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Adding...
                  </button>
                ) : (
                  <button onClick={handleAddToCart} className="btn btn-lg btn-success">
                    <i className="fa fa-shopping-cart me-2"></i>
                    Buy Now
                    <span itemProp="offers" itemScope itemType="http://schema.org/Offer">
                      <meta itemProp="priceCurrency" content="USD" />
                      <span itemProp="price">
                        ${presetPack.price} USD
                      </span>
                    </span>
                    {discount && (
                      <small> ({discount}% off!)</small>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mt-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/preset_packs">Preset Packs</a>
          </li>
          <li className="breadcrumb-item">
            <a href={`/preset_packs/manufacturers/${presetPack.manufacturer_slug}`}>
              {presetPack.manufacturer_name}
            </a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {presetPack.name}
          </li>
        </ol>
      </nav>

      <hr />

      {/* Content Section */}
      <div className="row">
        <div className="col-lg">
          <div
            dangerouslySetInnerHTML={{ __html: presetPack.page_text }}
            className="product-description"
          />
        </div>

        <div className="col-lg pull-right">
          {presetPack.youtube_link ? (
            <iframe
              width="560"
              height="315"
              src={presetPack.youtube_embed_url}
              frameBorder="0"
              allowFullScreen
              className="img-fluid"
            />
          ) : (
            <img
              src={presetPack.mid_image}
              alt={presetPack.name}
              width="450"
              className="pull-right img-fluid"
            />
          )}
        </div>
      </div>

      <CustomAudioPlayer />
    </div>
  );
});

const PresetPackShow = (props) => {
  return (
    <Provider store={store}>
      <PresetPackShowComponent {...props} />
    </Provider>
  );
};

export default PresetPackShow;